/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import logo from '/images/logo.svg';
import LegalBar from './legal-bar';
import type { FooterLegal } from '@/lib/content/types';

/**
 * Simplified checkout footer (CRCB2B-48): the white SRAM wordmark on the dark surface, plus the same
 * shared `LegalBar` the full footer uses — no columns, no social links, no newsletter CTA. Replaces
 * the previous light-chrome upstream-template footer wholesale (not an edit); see design.md
 * Decision 13.
 *
 * `legalContent` is fetched by id only (`footer-legal`) — the checkout footer never depends on the
 * content search index, the searchable flag, or folder membership. See `_checkout.tsx`'s loader.
 */
export default function CheckoutFooter({
    legalContent,
}: {
    legalContent: Promise<FooterLegal | null> | undefined;
}): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <footer className="mt-auto">
            <div className="bg-footer-background [--foreground:var(--base-core-white)] [--muted-foreground:var(--base-core-grey-300)]">
                <div className="section-container py-8">
                    <Link to="/">
                        <img src={logo} alt={t('logoAlt')} className="h-6 w-auto" />
                    </Link>
                </div>

                {legalContent && (
                    <Suspense fallback={null}>
                        <Await resolve={legalContent}>{(legal) => <LegalBar legal={legal} />}</Await>
                    </Suspense>
                )}
            </div>
        </footer>
    );
}
